/**
 * Executive Presentation View
 * Professional boardroom-style presentation for executive briefings
 * Designed to look like high-quality PowerPoint/Keynote slides
 */

import React, { useState, useEffect, useRef } from 'react';
import {
    Button,
    Progress,
    Tag,
    Spin,
    message,
    Tooltip,
    Row,
    Col,
    Statistic
} from 'antd';
import {
    LeftOutlined,
    RightOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    FullscreenOutlined,
    FullscreenExitOutlined,
    PrinterOutlined,
    DownloadOutlined,
    CalendarOutlined,
    TrophyOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    RiseOutlined,
    FallOutlined,
    LineChartOutlined,
    BarChartOutlined,
    ThunderboltFilled,
    CloseOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import bicollabLogo from '../../assets/bicollab-logo.png';

// Styled Components for Presentation
const PresentationContainer = styled.div`
    width: 100%;
    min-height: 100vh;
    background: #1a1a1a;
    position: relative;
    overflow: hidden;
`;

const SlideContainer = styled.div`
    width: 100%;
    max-width: 1400px;
    aspect-ratio: 16 / 9;
    margin: 40px auto;
    background: ${props => props.bg || 'white'};
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    position: relative;
    overflow: hidden;
    padding: 60px 80px;
    display: flex;
    flex-direction: column;

    ${props => props.fullscreen && `
        max-width: 100vw;
        height: 100vh;
        margin: 0;
        border-radius: 0;
        aspect-ratio: auto;
    `}
`;

const CoverLogo = styled.div`
    position: absolute;
    top: 28px;
    left: 32px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    z-index: 2;
    pointer-events: none;
    text-align: left;

    img {
        display: block;
        height: 44px;
        max-width: 360px;
        object-fit: contain;
    }
`;

const SlideHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 40px;
    padding-bottom: 20px;
    border-bottom: 3px solid ${props => props.accentColor || '#F16622'};

    .slide-number {
        font-size: 14px;
        color: #8c8c8c;
        font-weight: 600;
    }

    .company-logo {
        font-size: 20px;
        font-weight: 700;
        background: linear-gradient(135deg, #F16622 0%, #ff8c42 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
`;

const SlideTitle = styled.h1`
    font-size: ${props => props.large ? '56px' : '42px'};
    font-weight: 800;
    color: ${props => props.color || '#262626'};
    margin: 0 0 20px 0;
    line-height: 1.2;

    ${props => props.gradient && `
        background: linear-gradient(135deg, #F16622 0%, #ff8c42 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    `}
`;

const SlideSubtitle = styled.h2`
    font-size: 24px;
    color: #595959;
    font-weight: 400;
    margin: 0 0 40px 0;
    line-height: 1.4;
`;

const SlideContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: ${props => props.center ? 'center' : 'flex-start'};
`;

const NavigationControls = styled.div`
    position: fixed;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(10px);
    padding: 16px 24px;
    border-radius: 50px;
    display: flex;
    gap: 16px;
    align-items: center;
    z-index: 1000;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);

    .slide-indicator {
        color: white;
        font-weight: 600;
        font-size: 14px;
        min-width: 80px;
        text-align: center;
    }

    button {
        background: transparent;
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;

        &:hover {
            background: rgba(255, 255, 255, 0.1);
            border-color: #F16622;
            color: #F16622;
        }

        &:disabled {
            opacity: 0.3;
            cursor: not-allowed;
        }
    }
`;

const MetricCard = styled.div`
    background: ${props => props.bg || '#fafafa'};
    border-radius: 16px;
    padding: 32px;
    text-align: center;
    border: 2px solid ${props => props.borderColor || '#f0f0f0'};
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: ${props => props.accentColor || '#F16622'};
    }

    .metric-value {
        font-size: 64px;
        font-weight: 800;
        color: ${props => props.valueColor || '#262626'};
        line-height: 1;
        margin-bottom: 12px;
    }

    .metric-label {
        font-size: 18px;
        color: #595959;
        font-weight: 600;
        margin-bottom: 8px;
    }

    .metric-change {
        font-size: 16px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        color: ${props => props.changeColor || '#52c41a'};
    }
`;

const InsightBox = styled.div`
    background: linear-gradient(135deg, ${props => props.gradient || '#667eea 0%, #764ba2 100%'});
    border-radius: 16px;
    padding: 32px;
    color: white;
    margin-bottom: 20px;

    .insight-icon {
        font-size: 40px;
        margin-bottom: 16px;
        opacity: 0.9;
    }

    .insight-title {
        font-size: 22px;
        font-weight: 700;
        margin-bottom: 12px;
    }

    .insight-text {
        font-size: 16px;
        line-height: 1.6;
        opacity: 0.95;
    }
`;

const RecommendationItem = styled.div`
    display: flex;
    gap: 20px;
    padding: 20px;
    background: white;
    border-left: 4px solid ${props => props.color || '#F16622'};
    border-radius: 8px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .priority-badge {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: ${props => props.color || '#F16622'};
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        font-weight: 700;
        flex-shrink: 0;
    }

    .recommendation-content {
        flex: 1;

        .title {
            font-size: 18px;
            font-weight: 700;
            color: #262626;
            margin-bottom: 8px;
        }

        .description {
            font-size: 15px;
            color: #595959;
            line-height: 1.6;
        }

        .impact {
            font-size: 13px;
            color: #8c8c8c;
            margin-top: 8px;
            font-weight: 600;
        }
    }
`;

const ExecutivePresentationView = ({ briefingData, onClose }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [fullscreen, setFullscreen] = useState(false);
    const containerRef = useRef(null);

    // Parse briefing data
    const briefing = briefingData || {};
    const summary = typeof briefing.Summary === 'string' ? JSON.parse(briefing.Summary || '{}') : (briefing.Summary || {});

    // Map AI-generated recommendations to frontend format
    if (summary.recommendations && Array.isArray(summary.recommendations)) {
        summary.recommendations = summary.recommendations.map(rec => ({
            ...rec,
            // Map expectedImpact → impact for display
            impact: rec.expectedImpact || rec.impact || 'Impact not specified',
            // Map action → description if description is missing
            description: rec.description || rec.action || '',
            // Add color based on priority
            color: rec.priority === 'HIGH' ? '#ff4d4f' :
                   rec.priority === 'MEDIUM' || rec.priority === 'MED' ? '#faad14' :
                   '#52c41a'
        }));
    }

    // Map AI-generated risks to frontend format
    if (summary.risks && Array.isArray(summary.risks)) {
        summary.risks = summary.risks.map(risk => ({
            ...risk,
            // Ensure description is present
            description: risk.description || '',
            // Add display score
            displayScore: risk.score || (risk.level === 'HIGH' ? 85 : risk.level === 'MEDIUM' ? 70 : 50)
        }));
    }

    // Slide definitions
    const slides = [
        // SLIDE 1: Cover Slide
        {
            type: 'cover',
            title: 'Executive Briefing',
            subtitle: summary.title || 'Organizational Performance & Strategic Insights',
            date: new Date(briefing.CreatedAt || Date.now()).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            }),
            period: briefing.BriefingType || 'Quarterly Review'
        },

        // SLIDE 2: Executive Summary
        {
            type: 'executive-summary',
            title: 'Executive Summary',
            healthScore: briefing.OverallHealthScore || 0,
            summary: summary.executiveSummary || summary.overview || 'Comprehensive analysis of organizational performance across all modules.',
            keyMetrics: {
                modulesAnalyzed: briefing.ModulesCovered?.length || 0,
                totalReports: summary.metrics?.totalReports || 0,
                totalPages: summary.metrics?.totalPages || 0,
                healthScore: briefing.OverallHealthScore || 0
            }
        },

        // SLIDE 3: Key Metrics Dashboard
        {
            type: 'metrics-dashboard',
            title: 'Key Performance Indicators',
            metrics: [
                {
                    label: 'Overall Health Score',
                    value: briefing.OverallHealthScore || 0,
                    suffix: '/100',
                    change: summary.trends?.healthChange || '+5%',
                    trend: 'up',
                    color: briefing.OverallHealthScore >= 80 ? '#52c41a' : briefing.OverallHealthScore >= 60 ? '#1890ff' : '#faad14'
                },
                {
                    label: 'Modules Analyzed',
                    value: briefing.ModulesCovered?.length || 0,
                    change: summary.trends?.moduleChange || '+2',
                    trend: 'stable',
                    color: '#1890ff'
                },
                {
                    label: 'Critical Issues',
                    value: summary.metrics?.criticalIssues || 0,
                    change: summary.trends?.issuesChange || '-3',
                    trend: 'down',
                    color: summary.metrics?.criticalIssues > 5 ? '#ff4d4f' : '#52c41a'
                },
                {
                    label: 'Completion Rate',
                    value: summary.metrics?.completionRate || 95,
                    suffix: '%',
                    change: summary.trends?.completionChange || '+8%',
                    trend: 'up',
                    color: '#52c41a'
                }
            ]
        },

        // SLIDE 4: Performance Trends
        {
            type: 'trends-chart',
            title: 'Performance Trends',
            subtitle: 'Health Score Evolution Over Time',
            chartData: summary.trendsData || generateMockTrendData()
        },

        // SLIDE 5: Module Breakdown
        {
            type: 'module-breakdown',
            title: 'Module Performance Overview',
            modules: summary.moduleBreakdown || []
        },

        // SLIDE 6: Key Findings
        {
            type: 'key-findings',
            title: 'Key Findings & Insights',
            findings: summary.keyFindings || [
                {
                    title: 'Strong operational performance',
                    description: 'Overall system health remains above target thresholds.',
                    icon: <TrophyOutlined />,
                    gradient: '#52c41a, #73d13d'
                },
                {
                    title: 'Data quality improvements needed',
                    description: 'Some modules showing incomplete data coverage.',
                    icon: <WarningOutlined />,
                    gradient: '#faad14, #ffc53d'
                },
                {
                    title: 'Compliance adherence strong',
                    description: 'Regulatory compliance metrics exceed industry standards.',
                    icon: <CheckCircleOutlined />,
                    gradient: '#1890ff, #40a9ff'
                }
            ]
        },

        // SLIDE 7: Strategic Recommendations
        {
            type: 'recommendations',
            title: 'Strategic Recommendations',
            subtitle: 'Prioritized Action Items',
            recommendations: summary.recommendations || [
                {
                    priority: 'HIGH',
                    title: 'Enhance Data Collection Processes',
                    description: 'Implement automated data validation to improve coverage and accuracy across underperforming modules.',
                    impact: 'Expected 15-20% improvement in data quality scores',
                    color: '#ff4d4f'
                },
                {
                    priority: 'MED',
                    title: 'Expand Analytics Capabilities',
                    description: 'Deploy advanced predictive models to forecast trends and identify risks proactively.',
                    impact: 'Reduce incident response time by 30%',
                    color: '#faad14'
                },
                {
                    priority: 'LOW',
                    title: 'Optimize Reporting Workflows',
                    description: 'Streamline report generation processes to reduce manual effort and increase timeliness.',
                    impact: 'Save 10-15 hours per month in reporting overhead',
                    color: '#1890ff'
                }
            ]
        },

        // SLIDE 8: Risk Assessment
        {
            type: 'risk-assessment',
            title: 'Risk Assessment Matrix',
            risks: summary.risks || [
                { area: 'Data Quality', level: 'Medium', score: 65 },
                { area: 'Compliance', level: 'Low', score: 90 },
                { area: 'Operational Efficiency', level: 'Low', score: 85 },
                { area: 'Resource Allocation', level: 'Medium', score: 70 }
            ]
        },

        // SLIDE 9: Next Steps
        {
            type: 'next-steps',
            title: 'Next Steps & Timeline',
            steps: summary.nextSteps || [
                { quarter: 'Q1 2026', action: 'Deploy data validation framework', owner: 'Data Team' },
                { quarter: 'Q2 2026', action: 'Implement predictive analytics', owner: 'Analytics Team' },
                { quarter: 'Q3 2026', action: 'Optimize reporting workflows', owner: 'Operations Team' },
                { quarter: 'Q4 2026', action: 'Review and iterate improvements', owner: 'Leadership Team' }
            ]
        },

        // SLIDE 10: Closing Slide
        {
            type: 'closing',
            title: 'Thank You',
            subtitle: 'Questions & Discussion',
            contact: 'For detailed analysis, contact your BI Team'
        }
    ];

    const handlePrevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const handleNextSlide = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'ArrowRight' || e.key === ' ') {
            handleNextSlide();
        } else if (e.key === 'ArrowLeft') {
            handlePrevSlide();
        } else if (e.key === 'Escape') {
            setFullscreen(false);
        } else if (e.key === 'f' || e.key === 'F') {
            setFullscreen(!fullscreen);
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress);
        return () => document.removeEventListener('keydown', handleKeyPress);
    }, [currentSlide, fullscreen]);

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
    };

    const handleExportPDF = () => {
        message.info('PDF export feature coming soon');
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <PresentationContainer ref={containerRef}>
            {renderSlide(slides[currentSlide], currentSlide + 1, slides.length, fullscreen)}

            <NavigationControls>
                <Button
                    icon={<LeftOutlined />}
                    onClick={handlePrevSlide}
                    disabled={currentSlide === 0}
                    size="large"
                />
                <span className="slide-indicator">
                    {currentSlide + 1} / {slides.length}
                </span>
                <Button
                    icon={<RightOutlined />}
                    onClick={handleNextSlide}
                    disabled={currentSlide === slides.length - 1}
                    size="large"
                />
                <Tooltip title="Fullscreen (F)">
                    <Button
                        icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                        onClick={toggleFullscreen}
                        size="large"
                    />
                </Tooltip>
                <Tooltip title="Export PDF">
                    <Button
                        icon={<FilePdfOutlined />}
                        onClick={handleExportPDF}
                        size="large"
                    />
                </Tooltip>
                <Tooltip title="Print">
                    <Button
                        icon={<PrinterOutlined />}
                        onClick={handlePrint}
                        size="large"
                    />
                </Tooltip>
                <Tooltip title="Close">
                    <Button
                        icon={<CloseOutlined />}
                        onClick={() => onClose && onClose()}
                        size="large"
                    />
                </Tooltip>
            </NavigationControls>
        </PresentationContainer>
    );
};

// Helper function to render different slide types
const renderSlide = (slide, slideNumber, totalSlides, fullscreen) => {
    switch (slide.type) {
        case 'cover':
            return <CoverSlide slide={slide} fullscreen={fullscreen} />;
        case 'executive-summary':
            return <ExecutiveSummarySlide slide={slide} slideNumber={slideNumber} fullscreen={fullscreen} />;
        case 'metrics-dashboard':
            return <MetricsDashboardSlide slide={slide} slideNumber={slideNumber} fullscreen={fullscreen} />;
        case 'trends-chart':
            return <TrendsChartSlide slide={slide} slideNumber={slideNumber} fullscreen={fullscreen} />;
        case 'module-breakdown':
            return <ModuleBreakdownSlide slide={slide} slideNumber={slideNumber} fullscreen={fullscreen} />;
        case 'key-findings':
            return <KeyFindingsSlide slide={slide} slideNumber={slideNumber} fullscreen={fullscreen} />;
        case 'recommendations':
            return <RecommendationsSlide slide={slide} slideNumber={slideNumber} fullscreen={fullscreen} />;
        case 'risk-assessment':
            return <RiskAssessmentSlide slide={slide} slideNumber={slideNumber} fullscreen={fullscreen} />;
        case 'next-steps':
            return <NextStepsSlide slide={slide} slideNumber={slideNumber} fullscreen={fullscreen} />;
        case 'closing':
            return <ClosingSlide slide={slide} fullscreen={fullscreen} />;
        default:
            return null;
    }
};

// Individual slide components
const CoverSlide = ({ slide, fullscreen }) => (
    <SlideContainer bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" fullscreen={fullscreen}>
        <CoverLogo>
            <img
                src={bicollabLogo}
                alt="Insights from The BI Collaborative"
            />
        </CoverLogo>
        <SlideContent center>
            <div style={{ textAlign: 'center', color: 'white' }}>
                <div style={{ fontSize: '20px', marginBottom: '30px', opacity: 0.9, fontWeight: 600 }}>
                    <ThunderboltFilled style={{ marginRight: '8px' }} />
                    The BI Collaborative
                </div>
                <SlideTitle large style={{ color: 'white', fontSize: '72px', marginBottom: '30px' }}>
                    {slide.title}
                </SlideTitle>
                <SlideSubtitle style={{ color: 'rgba(255,255,255,0.9)', fontSize: '32px', marginBottom: '50px' }}>
                    {slide.subtitle}
                </SlideSubtitle>
                <div style={{ display: 'flex', gap: '40px', justifyContent: 'center', fontSize: '18px', opacity: 0.9 }}>
                    <div>
                        <CalendarOutlined style={{ marginRight: '8px' }} />
                        {slide.date}
                    </div>
                    <div>
                        {slide.period}
                    </div>
                </div>
            </div>
        </SlideContent>
    </SlideContainer>
);

const ExecutiveSummarySlide = ({ slide, slideNumber, fullscreen }) => (
    <SlideContainer fullscreen={fullscreen}>
        <SlideHeader accentColor="#F16622">
            <span className="company-logo">The BI Collaborative</span>
            <span className="slide-number">SLIDE {slideNumber}</span>
        </SlideHeader>
        <SlideTitle gradient>{slide.title}</SlideTitle>
        <SlideContent>
            <Row gutter={[24, 24]} style={{ marginBottom: '40px' }}>
                <Col span={18}>
                    <div style={{
                        fontSize: '18px',
                        lineHeight: '1.8',
                        color: '#595959',
                        padding: '30px',
                        background: '#fafafa',
                        borderRadius: '12px',
                        borderLeft: '4px solid #F16622'
                    }}>
                        {slide.summary}
                    </div>
                </Col>
                <Col span={6}>
                    <div style={{
                        textAlign: 'center',
                        padding: '30px',
                        background: slide.healthScore >= 80 ? '#f6ffed' : slide.healthScore >= 60 ? '#e6f7ff' : '#fff7e6',
                        borderRadius: '12px',
                        border: `2px solid ${slide.healthScore >= 80 ? '#52c41a' : slide.healthScore >= 60 ? '#1890ff' : '#faad14'}`
                    }}>
                        <div style={{ fontSize: '16px', color: '#8c8c8c', marginBottom: '12px', fontWeight: 600 }}>
                            OVERALL HEALTH
                        </div>
                        <div style={{
                            fontSize: '72px',
                            fontWeight: 800,
                            color: slide.healthScore >= 80 ? '#52c41a' : slide.healthScore >= 60 ? '#1890ff' : '#faad14',
                            lineHeight: 1
                        }}>
                            {slide.healthScore}
                        </div>
                        <div style={{ fontSize: '14px', color: '#8c8c8c', marginTop: '8px' }}>
                            / 100
                        </div>
                    </div>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Statistic
                        title={<span style={{ fontSize: '14px', color: '#8c8c8c' }}>Modules Analyzed</span>}
                        value={slide.keyMetrics.modulesAnalyzed}
                        valueStyle={{ fontSize: '36px', fontWeight: 700, color: '#1890ff' }}
                    />
                </Col>
                <Col span={8}>
                    <Statistic
                        title={<span style={{ fontSize: '14px', color: '#8c8c8c' }}>Total Reports</span>}
                        value={slide.keyMetrics.totalReports}
                        valueStyle={{ fontSize: '36px', fontWeight: 700, color: '#52c41a' }}
                    />
                </Col>
                <Col span={8}>
                    <Statistic
                        title={<span style={{ fontSize: '14px', color: '#8c8c8c' }}>Total Pages</span>}
                        value={slide.keyMetrics.totalPages}
                        valueStyle={{ fontSize: '36px', fontWeight: 700, color: '#722ed1' }}
                    />
                </Col>
            </Row>
        </SlideContent>
    </SlideContainer>
);

const MetricsDashboardSlide = ({ slide, slideNumber, fullscreen }) => (
    <SlideContainer fullscreen={fullscreen}>
        <SlideHeader accentColor="#F16622">
            <span className="company-logo">The BI Collaborative</span>
            <span className="slide-number">SLIDE {slideNumber}</span>
        </SlideHeader>
        <SlideTitle gradient>{slide.title}</SlideTitle>
        <SlideContent>
            <Row gutter={[24, 24]}>
                {slide.metrics.map((metric, idx) => (
                    <Col span={12} key={idx}>
                        <MetricCard accentColor={metric.color} bg="#fafafa" borderColor="#f0f0f0">
                            <div className="metric-value" style={{ color: metric.color }}>
                                {metric.value}{metric.suffix || ''}
                            </div>
                            <div className="metric-label">{metric.label}</div>
                            <div className="metric-change" style={{ color: metric.trend === 'up' ? '#52c41a' : metric.trend === 'down' ? '#ff4d4f' : '#8c8c8c' }}>
                                {metric.trend === 'up' && <RiseOutlined />}
                                {metric.trend === 'down' && <FallOutlined />}
                                {metric.change}
                            </div>
                        </MetricCard>
                    </Col>
                ))}
            </Row>
        </SlideContent>
    </SlideContainer>
);

const TrendsChartSlide = ({ slide, slideNumber, fullscreen }) => {
    const labels = slide.chartData?.labels || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const values = slide.chartData?.values || [75, 78, 82, 79, 85, 88];
    const maxValue = 100;

    return (
        <SlideContainer fullscreen={fullscreen}>
            <SlideHeader accentColor="#F16622">
                <span className="company-logo">The BI Collaborative</span>
                <span className="slide-number">SLIDE {slideNumber}</span>
            </SlideHeader>
            <SlideTitle gradient>{slide.title}</SlideTitle>
            <SlideSubtitle>{slide.subtitle}</SlideSubtitle>
            <SlideContent>
                <div style={{ padding: '40px', background: '#fafafa', borderRadius: '12px' }}>
                    {/* Simple bar chart visualization */}
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '24px', height: '350px', justifyContent: 'space-around' }}>
                        {labels.map((label, idx) => {
                            const value = values[idx] || 0;
                            const height = (value / maxValue) * 100;
                            return (
                                <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#F16622' }}>{value}</div>
                                    <div style={{
                                        width: '100%',
                                        height: `${height * 3}px`,
                                        background: 'linear-gradient(180deg, #F16622 0%, #ff8c42 100%)',
                                        borderRadius: '8px 8px 0 0',
                                        transition: 'all 0.3s ease',
                                        boxShadow: '0 -4px 12px rgba(241, 102, 34, 0.3)'
                                    }}></div>
                                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#595959' }}>{label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </SlideContent>
        </SlideContainer>
    );
};

const ModuleBreakdownSlide = ({ slide, slideNumber, fullscreen }) => (
    <SlideContainer fullscreen={fullscreen}>
        <SlideHeader accentColor="#F16622">
            <span className="company-logo">The BI Collaborative</span>
            <span className="slide-number">SLIDE {slideNumber}</span>
        </SlideHeader>
        <SlideTitle gradient>{slide.title}</SlideTitle>
        <SlideContent>
            <Row gutter={[16, 16]}>
                {(slide.modules.length > 0 ? slide.modules : getMockModules()).map((module, idx) => (
                    <Col span={12} key={idx}>
                        <div style={{
                            padding: '24px',
                            background: '#fafafa',
                            borderRadius: '12px',
                            borderLeft: `4px solid ${getHealthColor(module.healthScore || 75)}`
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <div style={{ fontSize: '20px', fontWeight: 700, color: '#262626' }}>
                                    {module.name || `Module ${idx + 1}`}
                                </div>
                                <div style={{
                                    fontSize: '32px',
                                    fontWeight: 800,
                                    color: getHealthColor(module.healthScore || 75)
                                }}>
                                    {module.healthScore || 75}
                                </div>
                            </div>
                            <Progress
                                percent={module.healthScore || 75}
                                strokeColor={getHealthColor(module.healthScore || 75)}
                                showInfo={false}
                                strokeWidth={8}
                            />
                            <div style={{ fontSize: '14px', color: '#8c8c8c', marginTop: '12px' }}>
                                {module.summary || 'Performance within expected parameters'}
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
        </SlideContent>
    </SlideContainer>
);

const KeyFindingsSlide = ({ slide, slideNumber, fullscreen }) => (
    <SlideContainer fullscreen={fullscreen}>
        <SlideHeader accentColor="#F16622">
            <span className="company-logo">The BI Collaborative</span>
            <span className="slide-number">SLIDE {slideNumber}</span>
        </SlideHeader>
        <SlideTitle gradient>{slide.title}</SlideTitle>
        <SlideContent>
            <Row gutter={[0, 24]}>
                {slide.findings.map((finding, idx) => (
                    <Col span={24} key={idx}>
                        <InsightBox gradient={finding.gradient}>
                            <div className="insight-icon">{finding.icon}</div>
                            <div className="insight-title">{finding.title}</div>
                            <div className="insight-text">{finding.description}</div>
                        </InsightBox>
                    </Col>
                ))}
            </Row>
        </SlideContent>
    </SlideContainer>
);

const RecommendationsSlide = ({ slide, slideNumber, fullscreen }) => (
    <SlideContainer fullscreen={fullscreen}>
        <SlideHeader accentColor="#F16622">
            <span className="company-logo">The BI Collaborative</span>
            <span className="slide-number">SLIDE {slideNumber}</span>
        </SlideHeader>
        <SlideTitle gradient>{slide.title}</SlideTitle>
        <SlideSubtitle>{slide.subtitle}</SlideSubtitle>
        <SlideContent>
            {slide.recommendations.map((rec, idx) => (
                <RecommendationItem key={idx} color={rec.color}>
                    <div className="priority-badge">{rec.priority}</div>
                    <div className="recommendation-content">
                        <div className="title">{rec.title}</div>
                        <div className="description">{rec.description}</div>
                        <div className="impact">Expected Impact: {rec.impact}</div>
                    </div>
                </RecommendationItem>
            ))}
        </SlideContent>
    </SlideContainer>
);

const RiskAssessmentSlide = ({ slide, slideNumber, fullscreen }) => (
    <SlideContainer fullscreen={fullscreen}>
        <SlideHeader accentColor="#F16622">
            <span className="company-logo">The BI Collaborative</span>
            <span className="slide-number">SLIDE {slideNumber}</span>
        </SlideHeader>
        <SlideTitle gradient>{slide.title}</SlideTitle>
        <SlideContent>
            <Row gutter={[16, 24]}>
                {slide.risks.map((risk, idx) => (
                    <Col span={12} key={idx}>
                        <div style={{
                            padding: '24px',
                            background: '#fafafa',
                            borderRadius: '12px',
                            borderTop: `4px solid ${getRiskColor(risk.level)}`
                        }}>
                            <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px', color: '#262626' }}>
                                {risk.area}
                            </div>
                            <Tag color={getRiskTagColor(risk.level)} style={{ marginBottom: '12px', fontSize: '13px', padding: '4px 12px' }}>
                                {risk.level} Risk
                            </Tag>
                            <Progress
                                percent={risk.score}
                                strokeColor={getRiskColor(risk.level)}
                                format={percent => `${percent}/100`}
                                strokeWidth={12}
                            />
                        </div>
                    </Col>
                ))}
            </Row>
        </SlideContent>
    </SlideContainer>
);

const NextStepsSlide = ({ slide, slideNumber, fullscreen }) => (
    <SlideContainer fullscreen={fullscreen}>
        <SlideHeader accentColor="#F16622">
            <span className="company-logo">The BI Collaborative</span>
            <span className="slide-number">SLIDE {slideNumber}</span>
        </SlideHeader>
        <SlideTitle gradient>{slide.title}</SlideTitle>
        <SlideContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {slide.steps.map((step, idx) => (
                    <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '24px',
                        padding: '20px 24px',
                        background: idx % 2 === 0 ? '#fafafa' : 'white',
                        borderRadius: '8px',
                        borderLeft: '4px solid #F16622'
                    }}>
                        <div style={{
                            width: '100px',
                            fontSize: '18px',
                            fontWeight: 700,
                            color: '#F16622',
                            flexShrink: 0
                        }}>
                            {step.quarter}
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '17px', fontWeight: 600, color: '#262626', marginBottom: '4px' }}>
                                {step.action}
                            </div>
                            <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                Owner: {step.owner}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </SlideContent>
    </SlideContainer>
);

const ClosingSlide = ({ slide, fullscreen }) => (
    <SlideContainer bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" fullscreen={fullscreen}>
        <SlideContent center>
            <div style={{ textAlign: 'center', color: 'white' }}>
                <SlideTitle large style={{ color: 'white', fontSize: '80px', marginBottom: '30px' }}>
                    {slide.title}
                </SlideTitle>
                <SlideSubtitle style={{ color: 'rgba(255,255,255,0.9)', fontSize: '32px', marginBottom: '60px' }}>
                    {slide.subtitle}
                </SlideSubtitle>
                <div style={{ fontSize: '18px', opacity: 0.9 }}>
                    {slide.contact}
                </div>
            </div>
        </SlideContent>
    </SlideContainer>
);

// Helper functions
const getHealthColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#1890ff';
    if (score >= 40) return '#faad14';
    return '#ff4d4f';
};

const getRiskColor = (level) => {
    if (level === 'Low') return '#52c41a';
    if (level === 'Medium') return '#faad14';
    return '#ff4d4f';
};

const getRiskTagColor = (level) => {
    if (level === 'Low') return 'success';
    if (level === 'Medium') return 'warning';
    return 'error';
};

const generateMockTrendData = () => ({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    values: [75, 78, 82, 79, 85, 88]
});

const getMockModules = () => [
    { name: 'Finance', healthScore: 88, summary: 'Strong performance with minimal issues' },
    { name: 'Human Resources', healthScore: 75, summary: 'Moderate performance, improving trends' },
    { name: 'Sales', healthScore: 82, summary: 'Good performance with growth potential' },
    { name: 'Operations', healthScore: 91, summary: 'Excellent performance across all metrics' }
];

export default ExecutivePresentationView;
